import { useQuery, QueryStatus } from "@tanstack/react-query";
import { Target, Zap, BarChart3, Users, type LucideIcon } from "lucide-react";

import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import type { OverviewAPIData } from "@/schema/brand-os/brand-intel";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import { cn, message } from "@/lib/utils";

type CardDef = {
  label: string;
  id: keyof Omit<OverviewAPIData, "channel_performance">;
  Icon: LucideIcon;
  iconClassName: string;
  barColor: string;
};

type CardItem = Omit<CardDef, "id"> & { score: number };

const cardDefs: CardDef[] = [
  { label: "Brand Health", id: "brand_health", Icon: Target, iconClassName: "bg-success/10 text-success", barColor: "bg-success" },
  { label: "Creative Velocity", id: "creative_velocity", Icon: Zap, iconClassName: "bg-warning/10 text-warning", barColor: "bg-warning" },
  { label: "Competitive Position", id: "competitive_position", Icon: BarChart3, iconClassName: "bg-destructive/10 text-destructive", barColor: "bg-destructive" },
  { label: "Audience Alignment", id: "audience_alignment", Icon: Users, iconClassName: "bg-blue-100/40 text-blue-600", barColor: "bg-blue-600" },
];

function ScoreCard({ label, score, Icon, iconClassName, barColor }: CardItem) {
  if (!score) return <LockedCard message={message} />;
  return (
    <Card>
      <CardContent className="px-5 space-y-3">
        <div className={cn("flex size-10 items-center justify-center rounded-xl", iconClassName)}>
          <Icon className="size-5" strokeWidth={2.5} />
        </div>
        <p className="text-3xl font-bold text-foreground">
          {Math.round(score)}
          <span className="text-sm text-muted-foreground font-medium ml-1">/ 100</span>
        </p>
        <CardDescription>{label}</CardDescription>
        <Progress value={Math.round(score)} className="h-1.5 bg-muted" indicatorClassName={barColor} />
      </CardContent>
    </Card>
  );
}

function ScoreContentGrid({ cards }: { cards: CardItem[] }) {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <ScoreCard key={card.label} {...card} />
      ))}
    </div>
  );
}

function OverviewScoreCardContent({ status, cards }: { status: QueryStatus; cards: CardItem[] }) {
  switch (status) {
    case "pending":
      return <OverviewScoreCardSkeleton />;
    case "error":
      return <LockedCard message={message} />;
    case "success":
      return <ScoreContentGrid cards={cards} />;
  }
}

export function OverviewScoreCards() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchOverview());

  const cards = cardDefs.map(({ id, ...config }) => ({ ...config, score: data?.[id] ?? 0 }));

  return <OverviewScoreCardContent status={status} cards={cards} />;
}

function OverviewScoreCardSkeleton() {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i}>
          <CardContent className="px-5 space-y-3">
            <Skeleton className="size-10 rounded-xl" />
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-1.5 w-full rounded-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
